import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "quang_cart_v2";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const addonsSignature = (addons = []) =>
  [...addons]
    .map((a) => `${a.group_id || ""}::${a.option_id || a.name}`)
    .sort()
    .join("|");

const itemKey = (item) =>
  `${item.product_id}|${addonsSignature(item.selected_addons)}|${(item.comment || "").trim()}`;

const computeUnitPrice = (item) =>
  Number(item.price || 0) +
  (item.selected_addons || []).reduce((s, a) => s + Number(a.price || 0), 0);

// ---------- Promotion computation (client side mirror of backend) ----------
const isPromoApplicable = (promo, itemsSubtotal) => {
  if (!promo) return false;
  if (promo.active === false) return false;
  const min = Number(promo.min_subtotal || 0);
  if (itemsSubtotal < min) return false;
  return true;
};

export const computeDiscounts = (items, promos, code = "") => {
  // Returns { lines: [{ cart_id, discount, label }], itemsSubtotal, finalSubtotal, total_discount, applied: [names], codeStatus }
  const lines = items.map((i) => ({
    cart_id: i.cart_id,
    product_id: i.product_id,
    unit: computeUnitPrice(i),
    qty: i.quantity,
    gross: computeUnitPrice(i) * i.quantity,
    discount: 0,
    label: "",
  }));
  const itemsSubtotal = lines.reduce((s, l) => s + l.gross, 0);

  const codeNorm = (code || "").trim().toUpperCase();
  const applicable = (promos || []).filter((p) => {
    const pCode = (p.code || "").trim().toUpperCase();
    if (pCode && pCode !== codeNorm) return false;
    if (!isPromoApplicable(p, itemsSubtotal)) return false;
    return true;
  });

  const applied = [];

  const noteApplied = (name) => {
    if (name && !applied.includes(name)) applied.push(name);
  };

  // Per-line
  lines.forEach((l) => {
    let lineTotal = l.gross;
    const labels = [];
    applicable.forEach((p) => {
      const t = p.type;
      const pids = p.product_ids || [];
      if (t === "product_fixed_price" && pids.includes(l.product_id)) {
        const newPrice = Number(p.value || 0);
        // We don't have unit-base separately — approximate: replace unit with (newPrice + addons)
        // Use l.unit as approximation; the backend will recompute precisely.
        const candidate = Math.max(0, newPrice * l.qty);
        if (candidate < lineTotal) { lineTotal = candidate; labels.push(p.name); }
      }
      if (t === "product_amount_off" && pids.includes(l.product_id)) {
        lineTotal = Math.max(0, lineTotal - Number(p.value || 0) * l.qty);
        labels.push(p.name);
      }
      if (t === "product_percent" && pids.includes(l.product_id)) {
        lineTotal = Math.max(0, lineTotal * (1 - Number(p.value || 0) / 100));
        labels.push(p.name);
      }
      if (t === "bogo" && pids.includes(l.product_id)) {
        const freeUnits = Math.floor(l.qty / 2);
        if (freeUnits > 0) {
          const effUnit = lineTotal / l.qty;
          lineTotal = Math.max(0, lineTotal - effUnit * freeUnits);
          labels.push(p.name);
        }
      }
    });
    l.discount = Math.round((l.gross - lineTotal) * 100) / 100;
    l.label = labels.join(" · ");
    labels.forEach(noteApplied);
  });

  const afterLine = lines.reduce((s, l) => s + (l.gross - l.discount), 0);

  // Global percent
  let globalPercent = 0;
  let globalName = "";
  applicable.forEach((p) => {
    if (p.type === "global_percent" && Number(p.value || 0) > globalPercent) {
      globalPercent = Number(p.value);
      globalName = p.name;
    }
  });
  let finalSubtotal = afterLine;
  if (globalPercent > 0 && finalSubtotal > 0) {
    finalSubtotal = afterLine * (1 - globalPercent / 100);
    noteApplied(globalName);
  }

  finalSubtotal = Math.round(finalSubtotal * 100) / 100;
  const totalDiscount = Math.round((itemsSubtotal - finalSubtotal) * 100) / 100;

  // Code status
  let codeStatus = null;
  if (codeNorm) {
    const matched = applicable.some((p) => (p.code || "").trim().toUpperCase() === codeNorm);
    codeStatus = matched ? "valid" : "invalid";
  }

  return { lines, itemsSubtotal, finalSubtotal, totalDiscount, applied, codeStatus };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [orderType, setOrderType] = useState("delivery");
  const [postalCode, setPostalCode] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [isNextDay, setIsNextDay] = useState(false);
  const [orderContextSet, setOrderContextSet] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [autoPromotions, setAutoPromotions] = useState([]);
  const [codePromoData, setCodePromoData] = useState(null); // validated code promo object

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        setItems(data.items || []);
        setOrderType(data.orderType || "delivery");
        setPostalCode(data.postalCode || "");
        setScheduledTime(data.scheduledTime || "");
        setScheduledDate(data.scheduledDate || "");
        setIsNextDay(!!data.isNextDay);
        setOrderContextSet(!!data.orderContextSet);
        setPromoCode(data.promoCode || "");
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ items, orderType, postalCode, scheduledTime, scheduledDate, isNextDay, orderContextSet, promoCode })
    );
  }, [items, orderType, postalCode, scheduledTime, scheduledDate, isNextDay, orderContextSet, promoCode]);

  // Load auto-applied promotions on mount + refresh hook
  const reloadAutoPromotions = useCallback(async () => {
    try {
      const { data } = await api.get("/promotions/active");
      setAutoPromotions(Array.isArray(data) ? data : []);
    } catch (e) {
      setAutoPromotions([]);
    }
  }, []);

  useEffect(() => { reloadAutoPromotions(); }, [reloadAutoPromotions]);

  const addItem = (product, opts = {}) => {
    const selected_addons = opts.selected_addons || [];
    const comment = (opts.comment || "").trim();
    const quantity = Math.max(1, Number(opts.quantity || 1));
    const newLine = {
      cart_id: uid(),
      product_id: product.id,
      name: product.name,
      price: Number(product.price || 0),
      image_url: product.image_url || "",
      selected_addons,
      comment,
      quantity,
    };
    const key = itemKey(newLine);
    setItems((prev) => {
      const found = prev.find((i) => itemKey(i) === key);
      if (found) {
        return prev.map((i) => (itemKey(i) === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, newLine];
    });
  };

  const removeItem = (cartId) =>
    setItems((prev) => prev.filter((i) => i.cart_id !== cartId));

  const updateQty = (cartId, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (i.cart_id === cartId ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
    );

  const clear = () => { setItems([]); setPromoCode(""); setCodePromoData(null); };

  const itemsSubtotal = useMemo(
    () => items.reduce((s, i) => s + computeUnitPrice(i) * i.quantity, 0),
    [items]
  );

  // Combine auto promos + validated code promo (if any)
  const effectivePromos = useMemo(() => {
    const list = [...autoPromotions];
    if (codePromoData) list.push(codePromoData);
    return list;
  }, [autoPromotions, codePromoData]);

  const discountInfo = useMemo(
    () => computeDiscounts(items, effectivePromos, promoCode),
    [items, effectivePromos, promoCode]
  );

  const subtotal = discountInfo.finalSubtotal; // after all discounts

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const setOrderContext = ({ orderType: ot, postalCode: pc, scheduledTime: st, scheduledDate: sd, isNextDay: ind }) => {
    if (ot !== undefined) setOrderType(ot);
    if (pc !== undefined) setPostalCode(pc);
    if (st !== undefined) setScheduledTime(st);
    if (sd !== undefined) setScheduledDate(sd);
    if (ind !== undefined) setIsNextDay(!!ind);
    setOrderContextSet(true);
  };

  const resetOrderContext = () => setOrderContextSet(false);

  const applyPromoCode = useCallback(async (code) => {
    const codeNorm = (code || "").trim().toUpperCase();
    if (!codeNorm) {
      setPromoCode("");
      setCodePromoData(null);
      return { ok: false, message: "Code vide" };
    }
    try {
      const { data } = await api.post("/promotions/validate-code", {
        code: codeNorm,
        items_subtotal: itemsSubtotal,
      });
      setPromoCode(codeNorm);
      setCodePromoData(data);
      return { ok: true, promo: data };
    } catch (e) {
      setPromoCode("");
      setCodePromoData(null);
      return { ok: false, message: e?.response?.data?.detail || "Code invalide" };
    }
  }, [itemsSubtotal]);

  const clearPromoCode = () => { setPromoCode(""); setCodePromoData(null); };

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    clear,
    subtotal,
    itemsSubtotal,
    count,
    orderType,
    setOrderType,
    postalCode,
    setPostalCode,
    scheduledTime,
    setScheduledTime,
    scheduledDate,
    setScheduledDate,
    isNextDay,
    setIsNextDay,
    orderContextSet,
    setOrderContext,
    resetOrderContext,
    computeUnitPrice,
    autoPromotions,
    reloadAutoPromotions,
    promoCode,
    applyPromoCode,
    clearPromoCode,
    codePromoData,
    discountInfo,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
